import { RenderFormat, ViewerDocumentKind, ViewerDownloadTargetKind, ViewerOpenContextKind, type ContentViewerSession } from '@/content-viewer/store/schemas'
import type { PaxtonHighlightArea } from '@/components/react-pdf-viewer/schema/paxton-highlight-area'
import { buildDeletedUserFileViewerSession } from '@/user_files/files/utils/build-deleted-user-file-viewer-session'
import { buildUserFileRendererConfig } from '@/user_files/files/utils/build-user-file-renderer-config'
import { buildUserFileViewerSession } from '@/user_files/files/utils/build-user-file-viewer-session'
import { createViewerSession } from '@/content-viewer/utils/create-viewer-session'
import type { UserFileRead } from '@/uppy-file-upload/schemas'
import { type MedSummaryReference } from '@/medical-billing-summaries/references/store/schemas'
import type { MedSummarySourceRef } from './schemas'

type BuildMedSummaryViewerSessionArgs = {
  source: MedSummarySourceRef
  reference?: MedSummaryReference
  userFile?: UserFileRead | null
  highlights?: PaxtonHighlightArea[]
  startingPage?: number
  // Asset surface, non-PDF source: open the stored conversion the file was extracted
  // against (AI-94) so highlight geometry lines up. Download still serves the original.
  convertedPdf?: { assetId: string; userFileId: string }
}

export function buildMedSummaryViewerSession(args: BuildMedSummaryViewerSessionArgs): ContentViewerSession | null {
  const { source, reference, userFile, highlights, startingPage, convertedPdf } = args
  const context = reference
    ? {
        kind: ViewerOpenContextKind.MED_SUMMARY_REFERENCE as const,
        referenceId: reference.id,
        sourceId: source.source_id,
      }
    : {
        kind: ViewerOpenContextKind.MED_SUMMARY_SOURCE as const,
        sourceId: source.source_id,
      }

  if (convertedPdf) {
    return createViewerSession({
      document: {
        kind: ViewerDocumentKind.MED_SUMMARY_CONVERTED_PDF,
        assetId: convertedPdf.assetId,
        userFileId: convertedPdf.userFileId,
        title: source.display_name,
        downloadTarget: {
          kind: ViewerDownloadTargetKind.USER_FILE_ORIGINAL,
          userFileId: convertedPdf.userFileId,
        },
      },
      context,
      presentation: {
        ...(highlights ? { pdfHighlights: highlights } : {}),
        ...(startingPage !== undefined ? { startingPage } : {}),
      },
    })
  }

  // The record is gone: open anyway so the pane renders the deleted state. See
  // buildDeletedUserFileViewerSession for what an absent record does and does not prove.
  if (!userFile) {
    return buildDeletedUserFileViewerSession({ userFileId: source.source_id, title: source.display_name, context })
  }

  const baseRenderer = buildUserFileRendererConfig(userFile)
  if (!baseRenderer) return null

  // Med source highlighting is PDF-only today. Non-PDF sources need a separate
  // presentation primitive before highlights or startingPage can apply.
  const renderer =
    baseRenderer.format === RenderFormat.PDF
      ? {
          ...baseRenderer,
          ...(highlights ? { highlights } : {}),
          ...(startingPage !== undefined ? { startingPage } : {}),
        }
      : baseRenderer

  return buildUserFileViewerSession({ userFile, renderer, context })
}
